import React, { createContext } from 'react';
import { styled } from '../theming';
import PropTypes from 'prop-types';
import { responsiveProp, ResponsiveProp } from '../utils/responsiveProp';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';

type PlatformVisibility = 'mobile' | 'desktop';

type StyledNavBarItemProps = {
  isVisible?: ResponsiveProp<boolean>;
};

export const StyledNavBarItem = styled.div<StyledNavBarItemProps>`
  width: 100%;

  @media (min-width: ${tokens.viewports.breakpoints.small}) {
    width: auto;
  }

  ${({ isVisible }) => ({
    ...responsiveProp(isVisible, (isVisible) => ({
      display: isVisible ? 'inline-flex' : 'none',
    })),
  })}
`;

const getPlatformVisibilityResponsiveProps = (
  platformVisibility: PlatformVisibility | undefined
) => {
  if (platformVisibility === 'mobile') {
    return { xxxsmall: true, small: false };
  } else if (platformVisibility === 'desktop') {
    return { xxxsmall: false, small: true };
  }
  return true;
};

/**
 * This Context is set to `true` for all descendants of `NavBarItem`.
 * At the time of writing, this Context is used by `DropdownMenu`. That component is styled slightly differently
 * when rendered inside the NavBar.
 */
export const IsDescendantOfNavBarItemContext = createContext(false);

export type NavBarItemProps = {
  children: React.ReactNode;
  platformVisibility?: PlatformVisibility;
};

const _NavBarItem = (
  { children, platformVisibility }: NavBarItemProps,
  ref?: React.Ref<HTMLDivElement>
): JSX.Element => {
  return (
    <StyledNavBarItem
      ref={ref}
      isVisible={getPlatformVisibilityResponsiveProps(platformVisibility)}
    >
      <IsDescendantOfNavBarItemContext.Provider value={true}>
        {children}
      </IsDescendantOfNavBarItemContext.Provider>
    </StyledNavBarItem>
  );
};

export const NavBarItem = React.forwardRef(_NavBarItem);

NavBarItem.propTypes = {
  children: PropTypes.node.isRequired,
  platformVisibility: PropTypes.oneOf(['mobile', 'desktop']),
};
